import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import {
  faArrowLeft,
  faBriefcase,
  faBuilding,
  faChartLine,
  faCheckCircle,
  faClock,
  faEnvelope,
  faLocationDot,
  faPhone,
  faShieldHalved,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import { LanguageService } from '../services/language.service';
import { CompanyDirectoryEntry } from './company-directory.models';
import { CompanyDirectoryService } from './company-directory.service';

@Component({
  selector: 'app-company-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, FontAwesomeModule],
  templateUrl: './company-detail.component.html',
  styleUrls: ['./company-directory.css']
})
export class CompanyDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(CompanyDirectoryService);
  readonly languageService = inject(LanguageService);

  readonly entry = signal<CompanyDirectoryEntry | null>(null);
  readonly loading = signal(true);
  error = '';
  logoFailed = false;
  readonly icons = {
    arrowLeft: faArrowLeft,
    briefcase: faBriefcase,
    building: faBuilding,
    chart: faChartLine,
    check: faCheckCircle,
    clock: faClock,
    email: faEnvelope,
    location: faLocationDot,
    phone: faPhone,
    shield: faShieldHalved,
    truck: faTruck,
    whatsapp: faWhatsapp
  };

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.error = this.text('Invalid company ID.', 'معرف شركة غير صالح.');
      this.loading.set(false);
      return;
    }
    this.service.getCompany(id).subscribe({
      next: (entry) => {
        this.entry.set(entry);
        this.loading.set(false);
      },
      error: () => {
        this.error = this.text(
          'Could not load company details.',
          'تعذر تحميل تفاصيل الشركة.'
        );
        this.entry.set(null);
        this.loading.set(false);
      }
    });
  }

  text(en: string, ar: string): string {
    return this.languageService.currentLanguage() === 'ar' ? ar : en;
  }

  trustScoreClass(score: number | null): string {
    if (score === null || score === undefined) return 'score-none';
    if (score >= 60) return 'score-high';
    if (score >= 30) return 'score-medium';
    return 'score-low';
  }

  completionRateColor(rate: number): string {
    if (rate >= 80) return 'var(--green)';
    if (rate >= 50) return 'var(--logo-amber)';
    return 'var(--red)';
  }

  companyTypeLabel(type: string | null): string {
    if (!type) return this.text('Company', 'شركة');
    const labels: Record<string, [string, string]> = {
      'FREIGHT_FORWARDER': ['Freight Forwarder', 'وكيل شحن'],
      'TRANSPORTER': ['Transporter', 'ناقل'],
      'BROKER': ['Broker', 'وسيط'],
      'SHIPPER': ['Shipper', 'شاحن'],
      'LOGISTICS_SERVICE': ['Logistics Service', 'خدمة لوجستية'],
    };
    const pair = labels[type];
    return pair ? this.text(pair[0], pair[1]) : type;
  }

  formatPercent(rate: number): string {
    return Math.round(rate) + '%';
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString(
      this.languageService.currentLanguage() === 'ar' ? 'ar-SA' : 'en-US',
      { month: 'short', day: 'numeric' }
    );
  }

  logoUrl(): string | null {
    const logo = this.entry()?.logo?.trim();
    return !this.logoFailed && logo ? logo : null;
  }

  logoInitial(): string {
    const entry = this.entry();
    const name = (entry?.name || this.companyTypeLabel(entry?.companyType ?? null) || 'C').trim();
    return name.charAt(0).toUpperCase();
  }

  contactName(entry: CompanyDirectoryEntry): string {
    return entry.primaryContact?.name?.trim() || this.text('Primary contact', 'جهة الاتصال الرئيسية');
  }

  emailHref(entry: CompanyDirectoryEntry): string | null {
    const email = entry.primaryContact?.email?.trim();
    return email ? `mailto:${email}` : null;
  }

  phoneHref(entry: CompanyDirectoryEntry): string | null {
    const phone = this.normalizedPhone(entry.primaryContact?.phone);
    return phone ? `tel:${phone}` : null;
  }

  whatsappHref(entry: CompanyDirectoryEntry): string | null {
    const phone = this.whatsappPhone(entry.primaryContact?.phone);
    return phone ? `https://wa.me/${phone}` : null;
  }

  private normalizedPhone(phone: string | null | undefined): string | null {
    const trimmed = phone?.trim();
    if (!trimmed) return null;
    const normalized = trimmed.replace(/[^\d+]/g, '');
    return normalized || null;
  }

  private whatsappPhone(phone: string | null | undefined): string | null {
    const normalized = this.normalizedPhone(phone);
    if (!normalized) return null;
    const digits = normalized.replace(/\D/g, '');
    return digits || null;
  }
}
